import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { useContent } from '../../../contexts/ContentContext';
import { useTranslation } from '../../../i18n';
import BankDetailsSection from './BankDetailsSection';

const RSVPForm = ({ guestData, onSubmit, isSubmitting }) => {
  const { content } = useContent();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    guestName: guestData?.guestName || '',
    email: '',
    phone: guestData?.phone || '',
    attending: null,
    attendingCeremony: false,
    attendingReception: false,
    numberOfGuests: guestData?.numberOfGuests || 1,
    hasDietRestriction: false,
    dietaryRestrictions: '',
    specialRequests: '',
  });

  const [errors, setErrors] = useState({});

  const ceremonyEvent = content?.rsvp?.events?.[0];
  const receptionEvent = content?.rsvp?.events?.[1];
  const bank = content?.bank;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.guestName?.trim()) {
      newErrors.guestName = t('validation.nameRequired');
    }

    if (formData.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('validation.invalidEmail');
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = t('validation.phoneRequired');
    } else if (!/^\+?[\d\s-()]+$/.test(formData.phone)) {
      newErrors.phone = t('validation.invalidPhone');
    }

    if (formData.attending === null) {
      newErrors.attending = t('validation.attendanceRequired');
    }

    if (formData.attending === true) {
      if (!formData.attendingCeremony && !formData.attendingReception) {
        newErrors.sessions = t('validation.selectSession');
      }
      if (formData.hasDietRestriction && !formData.dietaryRestrictions?.trim()) {
        newErrors.dietaryRestrictions = t('validation.dietDetailsRequired');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
      {/* Guest Info */}
      <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-romantic-md border border-border">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="User" size={20} color="var(--color-primary)" />
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
            {t('rsvp.guestInfo')}
          </h2>
        </div>

        <div className="space-y-4 md:space-y-5">
          <Input
            label={t('rsvp.fullName')}
            type="text"
            placeholder={t('rsvp.enterName')}
            value={formData.guestName}
            onChange={(e) => handleInputChange('guestName', e.target.value)}
            error={errors.guestName}
            required
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            <Input
              label={t('rsvp.emailAddress')}
              type="email"
              placeholder={t('rsvp.enterEmail')}
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              error={errors.email}
            />
            <Input
              label={t('rsvp.phoneNumber')}
              type="tel"
              placeholder={t('rsvp.enterPhone')}
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              error={errors.phone}
              required
            />
          </div>
        </div>
      </div>

      {/* Will Attend? */}
      <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-romantic-md border border-border">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="Calendar" size={20} color="var(--color-primary)" />
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
            {t('rsvp.willYouAttend')}
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <button
            type="button"
            onClick={() => handleInputChange('attending', true)}
            className={`p-4 md:p-6 rounded-xl border-2 transition-all text-center ${
              formData.attending === true
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-background text-muted-foreground hover:border-primary/50'
            }`}
          >
            <Icon name="Check" size={28} className="mx-auto mb-2" />
            <div className="font-semibold text-sm md:text-base">{t('rsvp.yesAttend')}</div>
          </button>
          <button
            type="button"
            onClick={() => handleInputChange('attending', false)}
            className={`p-4 md:p-6 rounded-xl border-2 transition-all text-center ${
              formData.attending === false
                ? 'border-destructive bg-destructive/10 text-destructive'
                : 'border-border bg-background text-muted-foreground hover:border-destructive/50'
            }`}
          >
            <Icon name="X" size={28} className="mx-auto mb-2" />
            <div className="font-semibold text-sm md:text-base">{t('rsvp.noAttend')}</div>
          </button>
        </div>
        {errors.attending && (
          <p className="text-sm text-error flex items-center gap-2 mt-3">
            <Icon name="AlertCircle" size={16} />
            {errors.attending}
          </p>
        )}
      </div>

      {/* If attending: Sessions + Companions + Diet */}
      {formData.attending === true && (
        <>
          {/* Sessions */}
          <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-romantic-md border border-border">
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Calendar" size={20} color="var(--color-primary)" />
              </div>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                {t('rsvp.whichSession')}
              </h2>
            </div>

            <div className="space-y-4">
              <div className="bg-background rounded-xl p-4 border border-border">
                <Checkbox
                  id="ceremony"
                  label={
                    <div className="ml-3">
                      <div className="font-semibold text-foreground">{ceremonyEvent?.title || t('rsvp.attendCeremony')}</div>
                      {(ceremonyEvent?.time || ceremonyEvent?.venue) && (
                        <div className="text-sm text-muted-foreground mt-1">
                          {ceremonyEvent.time}{ceremonyEvent.time && ceremonyEvent.venue && ' \u2022 '}{ceremonyEvent.venue}
                        </div>
                      )}
                    </div>
                  }
                  checked={formData.attendingCeremony}
                  onChange={(e) => handleInputChange('attendingCeremony', e.target.checked)}
                />
              </div>
              <div className="bg-background rounded-xl p-4 border border-border">
                <Checkbox
                  id="reception"
                  label={
                    <div className="ml-3">
                      <div className="font-semibold text-foreground">{receptionEvent?.title || t('rsvp.attendReception')}</div>
                      {(receptionEvent?.time || receptionEvent?.venue) && (
                        <div className="text-sm text-muted-foreground mt-1">
                          {receptionEvent.time}{receptionEvent.time && receptionEvent.venue && ' \u2022 '}{receptionEvent.venue}
                        </div>
                      )}
                    </div>
                  }
                  checked={formData.attendingReception}
                  onChange={(e) => handleInputChange('attendingReception', e.target.checked)}
                />
              </div>
              {errors.sessions && (
                <p className="text-sm text-error flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} />
                  {errors.sessions}
                </p>
              )}
            </div>
          </div>

          {/* Companions */}
          <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-romantic-md border border-border">
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Users" size={20} color="var(--color-primary)" />
              </div>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                {t('rsvp.totalAttending')}
              </h2>
            </div>
            <Select
              options={[1, 2, 3, 4, 5].map(n => ({
                value: n,
                label: `${n} ${n === 1 ? t('rsvp.person') : t('rsvp.persons')}`
              }))}
              value={formData.numberOfGuests}
              onChange={(value) => handleInputChange('numberOfGuests', parseInt(value, 10))}
            />
          </div>

          {/* Diet Restriction */}
          <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-romantic-md border border-border">
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="UtensilsCrossed" size={20} color="var(--color-primary)" />
              </div>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                {t('rsvp.dietRestriction')}
              </h2>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                type="button"
                onClick={() => handleInputChange('hasDietRestriction', true)}
                className={`p-3 rounded-xl border-2 transition-all text-center font-medium ${
                  formData.hasDietRestriction === true
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                }`}
              >
                {t('rsvp.dietYes')}
              </button>
              <button
                type="button"
                onClick={() => {
                  handleInputChange('hasDietRestriction', false);
                  handleInputChange('dietaryRestrictions', '');
                }}
                className={`p-3 rounded-xl border-2 transition-all text-center font-medium ${
                  formData.hasDietRestriction === false
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                }`}
              >
                {t('rsvp.dietNo')}
              </button>
            </div>

            {formData.hasDietRestriction && (
              <textarea
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-romantic"
                placeholder={t('rsvp.dietPlaceholder')}
                value={formData.dietaryRestrictions}
                onChange={(e) => handleInputChange('dietaryRestrictions', e.target.value)}
                rows={3}
              />
            )}
            {errors.dietaryRestrictions && (
              <p className="text-sm text-error flex items-center gap-2 mt-2">
                <Icon name="AlertCircle" size={16} />
                {errors.dietaryRestrictions}
              </p>
            )}
          </div>
        </>
      )}

      {/* Wishes — always shown */}
      <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-romantic-md border border-border">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
            {t('rsvp.wishesForCouple')}
          </h2>
        </div>
        <textarea
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-romantic"
          placeholder={t('rsvp.wishesPlaceholder')}
          value={formData.specialRequests}
          onChange={(e) => handleInputChange('specialRequests', e.target.value)}
          rows={4}
        />
      </div>

      {/* Bank Details — always shown if configured */}
      <BankDetailsSection bank={bank} />

      {/* Submit */}
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
        >
          {t('rsvp.submitRsvp')}
        </Button>
      </div>
    </form>
  );
};

export default RSVPForm;
